import React from 'react';
import { motion } from 'framer-motion';
import { Printer, Facebook, Instagram, Linkedin, Mail, Phone, MapPin, Clock, Award, Users, ArrowUp } from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Brand */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="space-y-6"
          >
            <div className="flex items-center">
              <img 
                src="/servicopy-logo-horizontal.png" 
                alt="Servi Copy del Pacífico Logo" 
                className="h-12 w-auto object-contain filter brightness-110"
              />
            </div>
            <p className="text-sm leading-relaxed text-gray-400">
              Más de 15 años ofreciendo soluciones profesionales de renta de equipos multifuncionales Canon y HP en BCS.
            </p>
            <div className="flex items-center space-x-3">
              <div className="flex -space-x-2">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-full border-2 border-gray-800"></div>
                <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-green-600 rounded-full border-2 border-gray-800"></div>
                <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full border-2 border-gray-800"></div>
              </div>
              <span className="text-xs text-gray-500">Clientes satisfechos</span>
            </div>
          </motion.div>

          {/* Services */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3 className="text-white font-bold text-lg mb-6 flex items-center">
              <div className="w-2 h-6 bg-gradient-to-b from-blue-500 to-cyan-600 rounded-full mr-3"></div>
              Servicios
            </h3>
            <ul className="space-y-3 text-sm">
              <li>
                <a href="#servicios" className="flex items-center group hover:text-white transition-colors duration-200">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-3 group-hover:scale-150 transition-transform duration-200"></div>
                  Renta de Equipos Canon y HP
                </a>
              </li>
              <li>
                <a href="#equipos" className="flex items-center group hover:text-white transition-colors duration-200">
                  <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-3 group-hover:scale-150 transition-transform duration-200"></div>
                  Catálogo de Equipos
                </a>
              </li>
              <li>
                <a href="#servicios" className="flex items-center group hover:text-white transition-colors duration-200">
                  <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mr-3 group-hover:scale-150 transition-transform duration-200"></div>
                  Mantenimiento Preventivo
                </a>
              </li>
              <li>
                <a href="#planes" className="flex items-center group hover:text-white transition-colors duration-200">
                  <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mr-3 group-hover:scale-150 transition-transform duration-200"></div>
                  Planes Flexibles
                </a>
              </li>
            </ul>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h3 className="text-white font-bold text-lg mb-6 flex items-center">
              <div className="w-2 h-6 bg-gradient-to-b from-emerald-500 to-green-600 rounded-full mr-3"></div>
              Contacto
            </h3>
            <ul className="space-y-4 text-sm">
              <li className="group">
                <div className="flex items-start space-x-3 p-3 rounded-lg bg-gray-800/50 hover:bg-gray-800 transition-all duration-200">
                  <div className="bg-gradient-to-r from-blue-500 to-cyan-600 p-2 rounded-lg">
                    <Phone className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <a href="tel:+526241109170" className="hover:text-white transition-colors duration-200 font-medium">
                      624 110 9170
                    </a>
                    <p className="text-xs text-gray-500 mt-1">ING. Bernardo Tapia</p>
                  </div>
                </div>
              </li>
              <li className="group">
                <div className="flex items-start space-x-3 p-3 rounded-lg bg-gray-800/50 hover:bg-gray-800 transition-all duration-200">
                  <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-2 rounded-lg">
                    <Mail className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <a href="mailto:servicopydelpacifico@hotmail.com" className="hover:text-white transition-colors duration-200 font-medium break-all">
                      servicopydelpacifico@hotmail.com
                    </a>
                  </div>
                </div>
              </li>
              <li className="group">
                <div className="flex items-start space-x-3 p-3 rounded-lg bg-gray-800/50 hover:bg-gray-800 transition-all duration-200">
                  <div className="bg-gradient-to-r from-emerald-500 to-green-600 p-2 rounded-lg">
                    <MapPin className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <span className="text-xs leading-relaxed">Calle 12 de octubre entre Ignacio Zaragoza y Melchor Ocampo, mz. 51 lt. 5, Local #4, Col. Idelfonso Green CP 23470, Cabo San Lucas, BCS</span>
                  </div>
                </div>
              </li>
            </ul>
          </motion.div>

          {/* Social & CTA */}
          
        </div>

        {/* Bottom */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="border-t border-gray-700/50 mt-12 pt-8"
        >
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-center md:text-left">
              <p className="text-sm text-gray-400">
                © {currentYear} <span className="text-white font-semibold">Servi Copy del Pacífico</span>. Todos los derechos reservados.
              </p>
            </div>
            <div className="flex items-center space-x-6 text-xs text-gray-500">
              <a href="#" className="hover:text-white transition-colors duration-200">Política de Privacidad</a>
              <a href="#" className="hover:text-white transition-colors duration-200">Términos de Servicio</a>
              <a href="#" className="hover:text-white transition-colors duration-200">Aviso Legal</a>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
